import { CheckIcon } from '@heroicons/react/20/solid'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form/dist/types'
import * as yup from 'yup'

import { Button, Divider, InputField, Text } from '../../common/designs'
import type { InheritanceStepCriteria } from '../Processes/types'
import {
  PROCESS_MATCHING_CRITERIA_MAX_STEPS_DEFAULT,
  PROCESS_MATCHING_CRITERIA_MAX_TIME_SEC_DEFAULT,
} from './constants'

const appAndWindowSchema = {
  app_name: yup
    .string()
    .test(
      'appNameOrWindowNameRequired',
      'Application or Full Screen Name is required',
      function (currentValue) {
        return this.parent.window_name || Boolean(currentValue)
      },
    ),
  window_name: yup
    .string()
    .test(
      'appNameOrWindowNameRequired',
      'Application or Full Screen Name is required',
      function (currentValue) {
        return this.parent.app_name || Boolean(currentValue)
      },
    ),
}

const nonNegativeInteger = (label: string) =>
  yup
    .number()
    .typeError(`${label} must be a number`)
    .integer(`${label} must be a whole number`)
    .min(0, `${label} cannot be negative`)

const formSchema = yup
  .object({
    ...appAndWindowSchema,
    max_step_distance: nonNegativeInteger('Max steps from process screen').default(
      PROCESS_MATCHING_CRITERIA_MAX_STEPS_DEFAULT,
    ),
    max_time_sec_distance: nonNegativeInteger('Max seconds from process screen').default(
      PROCESS_MATCHING_CRITERIA_MAX_TIME_SEC_DEFAULT,
    ),
  })
  .required()

const formSchemaWithDistanceMetrics = yup
  .object({
    ...appAndWindowSchema,
    max_step_distance: nonNegativeInteger('Max steps from process screen').required(),
    max_time_sec_distance: nonNegativeInteger('Max seconds from process screen').required(),
  })
  .required()

interface Props {
  formData: InheritanceStepCriteria
  formIndex?: number
  onSave: (formData: InheritanceStepCriteria) => void
  onDelete: () => void
  isDistanceMetricsEnabled: boolean
  /** Called after a successful Apply so the parent can clear “new row must apply” state. */
  onAppliedSuccess?: () => void
}

const StepMatchingCriteriaForm = ({
  formData,
  formIndex,
  onSave,
  onDelete,
  isDistanceMetricsEnabled,
  onAppliedSuccess,
}: Props) => {
  const [isAppliedShown, setIsAppliedShown] = useState(false)

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
  } = useForm<InheritanceStepCriteria>({
    resolver: yupResolver(
      isDistanceMetricsEnabled ? formSchemaWithDistanceMetrics : formSchema,
    ) as Resolver<InheritanceStepCriteria>,
  })

  useEffect(() => {
    reset(formData)
  }, [formData, reset])

  const onFormSubmit = (submitted: InheritanceStepCriteria) => {
    onSave(submitted)
    // Clear dirty immediately so "Applied" can show on the same click; otherwise a follow-up
    // effect can still see isDirty true and fight setIsAppliedShown until the next interaction.
    reset(submitted)
    setIsAppliedShown(true)
    onAppliedSuccess?.()
  }

  const isAppliedVisible = isAppliedShown && !isDirty

  return (
    <div className='flex w-full min-w-0 flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm ring-1 ring-black/[0.04]'>
      <div className='flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2'>
        <Text size='xs' weight={600} color='gray' className='uppercase tracking-wide'>
          {formIndex !== undefined ? `Rule ${formIndex}` : 'Rule'}
        </Text>
        <div className='flex shrink-0 flex-wrap gap-2'>
          {isAppliedVisible ? (
            <Button
              size='xs'
              variant='secondary'
              disabled
              className='!border-primary-200 !bg-primary-50 !text-primary-900 hover:!bg-primary-100 disabled:!border-primary-200 disabled:!bg-primary-50 disabled:!text-primary-900'
              iconStart={<CheckIcon className='size-3.5 text-primary-700' />}
            >
              Applied
            </Button>
          ) : (
            <Button size='xs' onClick={() => void handleSubmit(onFormSubmit)()}>
              Apply
            </Button>
          )}
          <Button size='xs' variant='destructive' onClick={onDelete}>
            Delete
          </Button>
        </div>
      </div>

      <div className='flex flex-col gap-3 md:flex-row md:items-end md:gap-0'>
        <div className='min-w-0 flex-1 md:pr-2'>
          <Controller
            name='app_name'
            control={control}
            render={({ field }) => (
              <InputField
                size='xxs'
                placeholder='e.g. SAP'
                label='Application name'
                error={errors.app_name?.message}
                {...field}
              />
            )}
          />
        </div>

        <div className='relative flex items-center md:hidden'>
          <div className='grow border-t border-primary-200/40' />
          <span className='shrink-0 rounded-full bg-primary-50/90 px-2.5 py-0.5 text-xs font-medium text-primary-700/85 ring-1 ring-primary-200/60'>
            or
          </span>
          <div className='grow border-t border-primary-200/40' />
        </div>

        <div className='relative hidden w-11 shrink-0 self-stretch py-1 md:flex md:justify-center'>
          <div className='absolute inset-y-2 left-1/2 w-px -translate-x-1/2 bg-primary-200/45' />
          <span className='relative z-10 my-auto rounded-full bg-primary-50/90 px-2 py-0.5 text-xs font-medium text-primary-700/85 ring-1 ring-primary-200/60'>
            or
          </span>
        </div>

        <div className='min-w-0 flex-1 md:pl-2'>
          <Controller
            name='window_name'
            control={control}
            render={({ field }) => (
              <InputField
                size='xxs'
                label='Full screen name'
                placeholder='e.g. SAP | Purchase Order'
                error={errors.window_name?.message}
                {...field}
              />
            )}
          />
        </div>
      </div>

      {isDistanceMetricsEnabled && (
        <>
          <Divider />
          <div className='grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-x-4 md:gap-y-2'>
            <Text size='xs' weight={600} color='gray' className='col-span-full leading-snug'>
              Distance limits
            </Text>
            <Controller
              name='max_step_distance'
              control={control}
              render={({ field }) => (
                <InputField
                  size='xxs'
                  label='Max steps from process screen'
                  type='number'
                  min={0}
                  step={1}
                  error={errors.max_step_distance?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='max_time_sec_distance'
              control={control}
              render={({ field }) => (
                <InputField
                  size='xxs'
                  label='Max seconds from process screen'
                  type='number'
                  min={0}
                  step={1}
                  error={errors.max_time_sec_distance?.message}
                  {...field}
                />
              )}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default StepMatchingCriteriaForm

